import { HULLS } from "@frontier/content/definitions";
import { CARGO_STACK_LIMIT } from "@frontier/content/cargo-stacks";
import type { HullId } from "@frontier/content/definitions";
import { HULL_DISPLAY_ART, HULL_DISPLAY_ASPECT } from "./hull-art";
import { credits } from "./ui-labels";
import "./ship-showcase.css";
import "./ship-supplies.css";

interface Props {
  onSupplies?: () => void;
  hullId: HullId;
  installed: HullId;
  owned: readonly HullId[];
  balance: string;
  components?: number;
  purchasingAllowed: boolean;
  busy: boolean;
  onInstall: (hullId: HullId) => Promise<void>;
  onPurchase: (hullId: HullId) => Promise<void>;
}

export function HullShowcase({
  onSupplies,
  hullId,
  installed,
  owned,
  balance,
  components = 0,
  purchasingAllowed,
  busy,
  onInstall,
  onPurchase,
}: Props) {
  const hull = HULLS[hullId];
  const isOwned = owned.includes(hullId);
  const equipped = hullId === installed;
  const shortfall = BigInt(hull.price) - BigInt(balance);
  const blocked =
    busy ||
    equipped ||
    (!isOwned &&
      (!purchasingAllowed ||
        shortfall > 0n ||
        components < hull.componentPrice));
  return (
    <section
      className="ship-showcase"
      aria-label={`Осмотр: ${hull.name}`}
      data-preview-hull={hullId}
    >
      <header
        className={`ship-identity ${onSupplies ? "ship-identity-with-supplies" : ""}`}
      >
        {onSupplies && (
          <button
            className="ship-supply-action"
            onClick={onSupplies}
            aria-label={`Трюм корабля «${hull.name}»`}
          >
            ТРЮМ <span aria-hidden="true">↗</span>
          </button>
        )}
        <span>
          {equipped
            ? "АКТИВНЫЙ КОРАБЛЬ"
            : isOwned
              ? "В ТВОЁМ АНГАРЕ"
              : "ВЕРФЬ / ОСМОТР КОРПУСА"}
        </span>
        <h1>{hull.name}</h1>
        <small>
          ТИР {"I".repeat(hull.tier)} · ЯЧЕЕК: {hull.equipmentSlots}
        </small>
      </header>
      <div className="ship-display">
        <dl className="ship-stats ship-stats-left">
          <div>
            <dt>КОРПУС</dt>
            <dd>{hull.hp}</dd>
          </div>
          <div>
            <dt>ЯЧЕЙКИ</dt>
            <dd>
              {hull.equipmentSlots}
              <small>общий пул</small>
            </dd>
          </div>
        </dl>
        <div
          className="ship-projection"
          style={{
            width: `min(64cqw, ${HULL_DISPLAY_ASPECT[hullId] * 100}cqh, 320px)`,
            aspectRatio: HULL_DISPLAY_ASPECT[hullId],
          }}
        >
          <div className="ship-platform" aria-hidden="true" />
          <img
            className="ship-render"
            src={HULL_DISPLAY_ART[hullId]}
            alt={`Перспективный вид: ${hull.name}`}
          />
        </div>
        <dl className="ship-stats ship-stats-right">
          <div>
            <dt>СКОРОСТЬ</dt>
            <dd>{hull.speed}</dd>
          </div>
          <div>
            <dt>ТРЮМ</dt>
            <dd>
              {hull.capacity}
              <small>×{CARGO_STACK_LIMIT} конт.</small>
            </dd>
          </div>
        </dl>
      </div>
      <div className="ship-details">
        <div className="ship-role">
          <p>{hull.role}</p>
          <span>{hull.limitation}</span>
        </div>
        <div className="ship-acquisition">
          {equipped ? (
            <span className="ship-ready">● ГОТОВ К ОТСТЫКОВКЕ</span>
          ) : (
            <button
              className="ship-install"
              aria-label={
                isOwned
                  ? `Установить корпус «${hull.name}»`
                  : `Купить корпус «${hull.name}»: ${credits(hull.price)} КР${hull.componentPrice ? ` + ${hull.componentPrice} КОМП.` : ""}`
              }
              disabled={blocked}
              onClick={() => {
                if (!blocked)
                  void (isOwned ? onInstall(hullId) : onPurchase(hullId));
              }}
            >
              <span>
                {isOwned
                  ? "ВЫБРАТЬ КОРАБЛЬ"
                  : `КУПИТЬ · ${credits(hull.price)} КР${hull.componentPrice ? ` + ${hull.componentPrice} КОМП.` : ""}`}
              </span>
              <small>
                {isOwned
                  ? "БЕСПЛАТНО · СБОРКА СОХРАНЯЕТСЯ"
                  : shortfall > 0n || components < hull.componentPrice
                    ? `Не хватает ${shortfall > 0n ? `${credits(shortfall.toString())} КР` : ""}${components < hull.componentPrice ? ` ${hull.componentPrice - components} КОМП.` : ""}`
                    : "ДОБАВИТЬ В АНГАР"}
              </small>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
